/**
 * The guided path from a change of circumstance to a released disclosure,
 * rendered in the docked chat panel.
 *
 * **Every stage, state, date and gate is read live from `GET /coc/playbook`.**
 * Nothing is carried in from the conversation. That is the rule rather than a
 * detail: the revised-LE cutoff moves whenever the closing date does, so a plan
 * captured in a reply and read back days later can name a route that has since
 * closed. The card re-reads on every mount for the same reason.
 *
 * The assistant's register read arrives as `artifact` and contributes exactly
 * two things per record: the narrative someone wrote, and the prong an auditor
 * tests. Nothing time-sensitive comes from it — not a date, not a route, not a
 * timeliness verdict — because those move after a reply is written and it is
 * replayed verbatim. `display_id` has to match before any of it is shown.
 *
 * **What runs from here, and what does not.** Sending for approval and
 * attaching a record to a draft take no extra input and are both reversible, so
 * they run inline. Everything else opens the LE/CD tab: rejecting and
 * withdrawing need a written reason, evidence needs a file picker, releasing is
 * irreversible, and approving is guarded by an evidence rule that lives in that
 * screen's own control. A chat card must not become a second approval surface
 * with weaker controls than the first.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  CircleDashed,
  CornerDownRight,
  ExternalLink,
  Loader2,
  Minus,
  ShieldCheck,
  X,
} from 'lucide-react';

import styles from '@/styles/components/coc-playbook.module.css';
import useToastNotifications from '@/hooks/useToastNotifications';
import { focusCocRecord } from '@/redux/slices/cocFocus';
import {
  useAttachCocsToDraftMutation,
  useGetCocPlaybookQuery,
  useSubmitCocMutation,
} from '@/redux/services/admin/disclosures';
import { disclosureErrorMessage, formatTridDate } from '@/utils/disclosureFormat';
import { resolveMiraLink } from '@/utils/miraNavLinks';

/** Stage state → the dot's glyph and its class. `not_applicable` is not
 *  `pending`: one is waiting its turn, the other does not apply to this file. */
const STATE_UI = {
  done: { Icon: Check, cls: 'done', sr: 'complete' },
  current: { Icon: CircleDashed, cls: 'current', sr: 'current stage' },
  blocked: { Icon: AlertTriangle, cls: 'blocked', sr: 'blocked' },
  pending: { Icon: CircleDashed, cls: 'pending', sr: 'not yet' },
  not_applicable: { Icon: Minus, cls: 'na', sr: 'does not apply' },
};

const stateUi = (state) => STATE_UI[state] ?? STATE_UI.pending;

/** Which subtab an action should land on when it cannot run inline. */
const ACTION_SUBTAB = {
  attach_evidence: 'coc',
  edit_coc: 'coc',
  approve_coc: 'coc',
  reject_coc: 'coc',
  withdraw_coc: 'coc',
  open_readiness: 'coc',
  open_worksheet: 'worksheet',
  draft_disclosure: 'disclosure',
  release_disclosure: 'disclosure',
};

/**
 * The actions that run here, each with the request it makes and what it says
 * afterwards. Everything else opens the screen that owns its dialog.
 *
 * **One entry per action, holding all three facts.** Whether it runs inline,
 * which mutation it calls and how it is worded were three separate lists that
 * had to agree; a kind present in one and missing from another failed quietly —
 * a button that spun and did nothing, or acted and said nothing. Adding an
 * action is now one entry or it does not appear at all.
 *
 * The wording matches the CoC tab's own toasts: `RecordCocForm` already says
 * "sent for approval" and `CocCard` already says "added to" for these exact
 * mutations, and one request pressed from two places must not be described two
 * ways. A failure names the operation rather than saying "that was refused",
 * because this card sits in a rail that scrolls and the button may be out of
 * sight by the time the toast is read.
 */
const INLINE_ACTIONS = {
  submit_coc: {
    call: ({ submitCoc, loanId, action }) =>
      submitCoc({ loanId, cocId: action.coc_id }),
    done: (a) => `${a.coc_ref} sent for approval`,
    description: 'A reviewer decides it from here.',
    failed: (a) => `Could not send ${a.coc_ref} for approval`,
  },
  attach_to_draft: {
    call: ({ attachToDraft, loanId, action }) =>
      attachToDraft({ loanId, ref: action.disclosure_ref, cocIds: [action.coc_id] }),
    done: (a) => `${a.coc_ref} added to ${a.disclosure_ref}`,
    failed: (a) => `Could not add ${a.coc_ref} to ${a.disclosure_ref}`,
  },
};

/** Does this action act here, or navigate? */
const runsInline = (action) =>
  Object.prototype.hasOwnProperty.call(INLINE_ACTIONS, action.kind);

/**
 * Identifies one action among all of a stage's, so a spinner can be pinned to
 * the button that was actually pressed.
 *
 * Both refs, not the first one present: a stage lists one action per record,
 * and keying on `coc_ref ?? disclosure_ref` collapses two rows the moment they
 * differ only by the draft they join.
 */
const actionKey = (action) =>
  `${action.kind}-${action.coc_ref ?? ''}-${action.disclosure_ref ?? ''}`;

/** Stable, so `stages` keeps its identity across renders while the query is
 *  still resolving and the memo below is not recomputed on every one. */
const EMPTY = [];

export default function CocPlaybook({ loanId, artifact, onClose }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { success: toastSuccess, error: toastError } = useToastNotifications();

  const { data: plan, isFetching, isError, error, refetch } = useGetCocPlaybookQuery(
    { loanId },
    /* Re-read on every mount. The card can reappear from chat history long
       after the reply was written, and a stale route is worse than a spinner. */
    { skip: !loanId, refetchOnMountOrArgChange: true }
  );

  const [submitCoc] = useSubmitCocMutation();
  const [attachToDraft] = useAttachCocsToDraftMutation();
  const [actionError, setActionError] = useState(null);

  /* WHICH action is in flight, not merely whether one is. The mutation hooks'
     own `isLoading` is one boolean per hook, and a stage lists one button per
     record — so a single flag put a spinner on every record's button and said
     all of them were being sent. Set before the await, cleared in `finally`. */
  const [running, setRunning] = useState(null);

  /* The stage the reader is looking at. Null means "follow the server", which
     is the default and what a fresh card shows. */
  const [browsing, setBrowsing] = useState(null);
  const stages = plan?.stages ?? EMPTY;
  const currentKey = plan?.current ?? null;

  const currentIndex = useMemo(() => {
    const at = stages.findIndex((s) => s.key === currentKey);
    // Every stage settled: show the last one rather than falling back to the
    // first, which would read as "start here" on a finished file.
    return at >= 0 ? at : Math.max(stages.length - 1, 0);
  }, [stages, currentKey]);

  /* Follow the plan when it advances. Acting on a stage refetches, and if the
     stage the reader was on is the one that just completed, staying put would
     show a finished step while the next thing to do sits behind an arrow. */
  useEffect(() => {
    setBrowsing(null);
  }, [currentKey]);

  const index = browsing ?? currentIndex;
  const stage = stages[index];

  const run = useCallback(
    async (action) => {
      const spec = INLINE_ACTIONS[action.kind];
      if (!spec) return;
      setActionError(null);
      setRunning(actionKey(action));
      try {
        await spec.call({ submitCoc, attachToDraft, loanId, action }).unwrap();
        /* Say so. The alternative is what this card used to do — nothing — and
           a stage whose wording changes without its position moving reads as
           if the button did not fire. That is the attach case exactly. */
        toastSuccess(
          spec.done(action),
          spec.description ? { description: spec.description } : {}
        );
        /* Follow the server again after acting.
         *
         * Not covered by the effect below: that one fires when `current`
         * changes *value*, and an action routinely changes what the stages SAY
         * without moving which one is current. Attaching the last approved
         * record left the reader parked on a stage that had become
         * inapplicable a moment earlier — reading as the thing to do next when
         * the thing to do next was two stages further on. */
        setBrowsing(null);
      } catch (err) {
        const message = disclosureErrorMessage(err);
        /* Both, deliberately. The toast is what gets noticed; the inline copy
           is what stays on screen beside the control that was refused, which a
           toast cannot do because it leaves. */
        toastError(spec.failed(action), { description: message });
        setActionError(message);
      } finally {
        setRunning(null);
      }
    },
    [attachToDraft, loanId, submitCoc, toastError, toastSuccess]
  );

  const open = useCallback(
    (action) => {
      const subtab = ACTION_SUBTAB[action.kind] ?? 'coc';
      const target = resolveMiraLink(
        `mira:page/fees-worksheet?applicationId=${loanId}&subtab=${subtab}`
      );
      // Unresolvable means no usable application id, so there is nowhere to
      // send the reader — better to do nothing than navigate to the wrong loan.
      if (!target) return;
      /* Name the record, not just the tab. "Approve COC-1" that lands on a
         register of collapsed cards has answered which screen and not which
         row. The id rides the store because a URL parameter would re-open the
         card on every later visit to this link. */
      if (action.coc_id && subtab === 'coc') {
        dispatch(focusCocRecord(action.coc_id));
      }
      navigate(target.path);
    },
    [dispatch, loanId, navigate]
  );

  /* One request at a time, because both mutations share a hook instance and a
     second fired mid-flight races the first's result. So the other buttons go
     inert — but only the pressed one says it is working. */
  const busy = running !== null;

  /* The assistant's account of each record, by ref — and only where its payload
     is about this file. A reply for another loan can still be in the stream,
     and matching on the Profile ID is what keeps its prose off these rows. */
  const notes = useMemo(() => {
    const sameFile =
      artifact?.display_id && plan?.display_id
        ? artifact.display_id === plan.display_id
        : false;
    if (!sameFile || !Array.isArray(artifact.rows)) return {};
    return Object.fromEntries(
      artifact.rows
        .filter((r) => r?.ref && (r.narrative || r.prong))
        .map((r) => [r.ref, { narrative: r.narrative, prong: r.prong }])
    );
  }, [artifact, plan?.display_id]);

  if (!loanId) return null;

  if (isFetching && !plan) {
    return (
      <div className={styles.card} aria-busy="true">
        <div className={styles.skeletonHead} />
        <div className={styles.skeletonLine} />
        <div className={styles.skeletonLineShort} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.card}>
        <p className={styles.error}>{disclosureErrorMessage(error)}</p>
        <button type="button" className={styles.ghostBtn} onClick={refetch}>
          Try again
        </button>
      </div>
    );
  }

  if (!plan) return null;

  if (!plan.available) {
    return (
      <div className={styles.card}>
        <div className={styles.head}>
          <ShieldCheck size={13} aria-hidden="true" />
          <span className={styles.headText}>
            {plan.display_id ? `${plan.display_id} · ` : ''}Change of circumstance
          </span>
        </div>
        <p className={styles.detail}>{plan.unavailable_reason}</p>
      </div>
    );
  }

  if (!stage) return null;

  const { Icon: StageIcon, cls } = stateUi(stage.state);

  return (
    <div className={styles.card}>
      <div className={styles.head}>
        <ShieldCheck size={13} aria-hidden="true" />
        {/* The loan, always. The panel outlives the screen it was opened from,
            so a card that does not name its own file can read as being about
            whichever loan happens to be in front of you. */}
        <span className={styles.headText}>
          {plan.display_id ? `${plan.display_id} · ` : ''}{plan.route_label}
        </span>
        {plan.binding_deadline ? (
          <span className={styles.headMeta}>
            by {formatTridDate(plan.binding_deadline)}
          </span>
        ) : null}
        {onClose ? (
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close the guided steps"
          >
            <X size={13} aria-hidden="true" />
          </button>
        ) : null}
      </div>

      <div className={styles.stage}>
        {/* Where the reader is, and — when they have arrowed off it — where the
            file actually stands. A settled stage shown without that reads as
            the thing to do next. */}
        <p className={styles.stepOf}>
          Step {index + 1} of {stages.length}
          {index !== currentIndex ? (
            <button
              type="button"
              className={styles.jumpBack}
              onClick={() => setBrowsing(null)}
            >
              go to step {currentIndex + 1}
            </button>
          ) : null}
        </p>
        <h4 className={styles.title}>
          <span className={`${styles.dot} ${styles[cls]}`} aria-hidden="true">
            <StageIcon size={11} />
          </span>
          {stage.title}
        </h4>

        {/* What is true, then why it reads that way, then what changes it.
            Three fields rather than one paragraph: a reader arrowing through
            five steps needs to find the reason without reading the sentence
            that states the state, and needs the action to stand apart from
            both. The server owns all three — see `playbook.py`. */}
        <p className={styles.detail}>{stage.detail}</p>
        {stage.why ? <p className={styles.why}>{stage.why}</p> : null}
        {stage.blocked_reason ? (
          <p className={styles.blockedNote}>{stage.blocked_reason}</p>
        ) : null}
        {stage.next_move ? (
          <p className={styles.nextMove}>
            <CornerDownRight size={12} aria-hidden="true" />
            <span>{stage.next_move}</span>
          </p>
        ) : null}
        {/* Whose step this is, where the reader cannot take it. Beside the
            instruction and not instead of it: what has to happen is still true,
            and somebody asking why a file is stuck needs to read that it waits
            on a reviewer rather than find the step missing. The server decides
            this — the actions it could not offer are already gone. */}
        {stage.owned_by ? (
          <p className={styles.ownedBy}>
            <ShieldCheck size={12} aria-hidden="true" />
            <span>This step is {stage.owned_by}&rsquo;s to take.</span>
          </p>
        ) : null}
        {stage.citation ? (
          <p className={styles.citation}>{stage.citation}</p>
        ) : null}

        {stage.rows.length > 0 ? (
          <ul className={styles.rows}>
            {stage.rows.map((row, i) => (
              <li key={`${row.coc_ref ?? row.line_code ?? 'row'}-${i}`} className={styles.row}>
                <p className={styles.rowHead}>{row.headline}</p>
                <p className={styles.rowDetail}>{row.detail}</p>
                {notes[row.coc_ref] ? (
                  <div className={styles.note}>
                    {notes[row.coc_ref].prong ? (
                      <p className={styles.noteProng}>
                        {notes[row.coc_ref].prong}
                      </p>
                    ) : null}
                    {notes[row.coc_ref].narrative ? (
                      <p className={styles.noteBody}>
                        {notes[row.coc_ref].narrative}
                      </p>
                    ) : null}
                  </div>
                ) : null}
                {row.due_by ? (
                  <p className={styles.rowDue}>
                    Due {formatTridDate(row.due_by)}
                    {typeof row.days_remaining === 'number' ? (
                      <span className={row.days_remaining < 0 ? styles.late : undefined}>
                        {row.days_remaining < 0
                          ? ` · ${Math.abs(row.days_remaining)} days past`
                          : ` · ${row.days_remaining} days left`}
                      </span>
                    ) : null}
                  </p>
                ) : null}
                <Actions
                  actions={row.actions}
                  busy={busy}
                  running={running}
                  onRun={run}
                  onOpen={open}
                />
              </li>
            ))}
          </ul>
        ) : null}

        <Actions
          actions={stage.actions}
          busy={busy}
          running={running}
          onRun={run}
          onOpen={open}
        />

        {actionError ? <p className={styles.error}>{actionError}</p> : null}
      </div>

      <div className={styles.nav}>
        <button
          type="button"
          className={styles.navBtn}
          onClick={() => setBrowsing(Math.max(index - 1, 0))}
          disabled={index === 0}
          aria-label="Previous step"
        >
          <ArrowLeft size={13} aria-hidden="true" />
        </button>

        <ol className={styles.dots}>
          {stages.map((s, i) => {
            const ui = stateUi(s.state);
            return (
              <li key={s.key}>
                <button
                  type="button"
                  className={`${styles.pip} ${styles[`pip${ui.cls}`]} ${
                    i === index ? styles.pipOn : ''
                  }`}
                  onClick={() => setBrowsing(i)}
                  aria-current={i === index ? 'step' : undefined}
                  aria-label={`Step ${i + 1}, ${s.title}: ${ui.sr}`}
                />
              </li>
            );
          })}
        </ol>

        <button
          type="button"
          className={styles.navBtn}
          onClick={() => setBrowsing(Math.min(index + 1, stages.length - 1))}
          disabled={index >= stages.length - 1}
          aria-label="Next step"
        >
          <ArrowRight size={13} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

/**
 * A stage's or a row's actions.
 *
 * The two inline ones are buttons; the rest are links in behaviour, so they
 * carry the external-link affordance rather than looking like they will act
 * here and then navigating away.
 */
function Actions({ actions, busy, running, onRun, onOpen }) {
  if (!actions?.length) return null;
  return (
    <div className={styles.actions}>
      {actions.map((action) => {
        const inline = runsInline(action);
        const key = actionKey(action);
        const spinning = inline && running === key;
        return (
          <button
            key={key}
            type="button"
            className={inline ? styles.primaryBtn : styles.ghostBtn}
            onClick={() => (inline ? onRun(action) : onOpen(action))}
            disabled={inline && busy}
            aria-busy={spinning || undefined}
          >
            {spinning ? (
              <Loader2 size={12} className={styles.spin} aria-hidden="true" />
            ) : null}
            {action.label}
            {inline ? null : <ExternalLink size={11} aria-hidden="true" />}
          </button>
        );
      })}
    </div>
  );
}
